/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { Grid, Header } from '@mairistem/composants';

import type { Individu } from '@/types';

import PanelButtonEdition from './PanelButtonEdition';
import InformationIndividuModal from './InformationIndividuModal';

interface InformationIndividuProps {
  identifiant: string | number;
  parent?: boolean;
  title: string;
  titleModal: string;
  'data-testid': string;
  recense?: Partial<Individu>;
  updated: boolean;
  filiationInconnue?: boolean;
  dateRecensement?: string;
  dateNaissanceRecense?: {
    anneeNaissance: number;
    moisNaissance: number;
    jourNaissance: number;
  };
  onFiliationInconnueChange?: (isUnknown: boolean) => void;
  onChange: (data: any) => void;
  onSave: () => Promise<void>;
  onRollback: () => void;
}

const InformationIndividu = (
  props: InformationIndividuProps & Partial<Individu>,
) => {
  const {
    identifiant,
    minuteNaissance,
    heureNaissance,
    jourNaissance,
    moisNaissance,
    anneeNaissance,
    typeDateNaissance,
    sexe,
    nomFamille,
    nomUsage,
    prenom,
    prenomsComplementaires,
    adresseNaissance,
    identifiantNationalite,
    parent = false,
    title,
    titleModal,
    'data-testid': dataTestId,
    recense,
    updated,
    filiationInconnue,
    dateRecensement,
    dateNaissanceRecense,
    onFiliationInconnueChange,
    onChange,
    onSave,
    onRollback,
  } = props;

  const [modalEditionOpen, setModalEditionOpen] = React.useState(false);

  const {
    libelleCodePostal,
    libelleCommune,
    libellePays,
    codeDepartement,
    libelleDepartement,
  } = recense?.adresseNaissance || {};

  const dateNaissance = React.useMemo(
    () =>
      dayjs(
        `${recense?.anneeNaissance}-${recense?.moisNaissance}-${recense?.jourNaissance}`,
      ).format('DD/MM/YYYY'),
    [recense?.anneeNaissance, recense?.moisNaissance, recense?.jourNaissance],
  );

  // Formatage du département pour l'affichage
  const formattedDepartement = React.useMemo(() => {
    const code = codeDepartement || '';
    const libelle = libelleDepartement || '';
    return `${code}${!_.isEmpty(libelle) ? ` (${libelle})` : ''}`;
  }, [codeDepartement, libelleDepartement]);

  return (
    <>
      <Grid.Row>
        <Header
          as="h5"
          color="blue"
          style={{ margin: 0 }}
        >{`${title} ${parent && _.isNil(identifiant) ? ' - Inconnu' : ''}`}</Header>
        <PanelButtonEdition
          onClick={() => {
            setModalEditionOpen(true);
          }}
          data-testid={dataTestId}
        />
        {modalEditionOpen && (
          <InformationIndividuModal
            minuteNaissance={minuteNaissance}
            heureNaissance={heureNaissance}
            jourNaissance={jourNaissance}
            moisNaissance={moisNaissance}
            anneeNaissance={anneeNaissance}
            typeDateNaissance={typeDateNaissance}
            sexe={sexe}
            nomFamille={nomFamille}
            nomUsage={nomUsage}
            prenom={prenom}
            prenomsComplementaires={prenomsComplementaires}
            adresseNaissance={adresseNaissance}
            identifiantNationalite={identifiantNationalite}
            parent={parent}
            title={titleModal}
            updated={updated}
            filiationInconnue={filiationInconnue}
            dateRecensement={dateRecensement}
            dateNaissanceRecense={dateNaissanceRecense}
            onFiliationInconnueChange={onFiliationInconnueChange}
            onChange={onChange}
            onSave={onSave}
            onClose={() => {
              setModalEditionOpen(false);
            }}
            onCancel={() => {
              onRollback();
              setModalEditionOpen(false);
            }}
          />
        )}
      </Grid.Row>
      {!(parent && _.isNil(recense?.identifiant)) ? (
        <>
          <Grid.Row>
            <Grid.Column
              mobile={16}
              tablet={12}
              computer={8}
              largeScreen={5}
              widescreen={4}
            >
              <span>
                <b>Né le : </b>
              </span>
              <span>
                {dateNaissance}
                {!_.isNil(recense?.heureNaissance) &&
                  !_.isNil(recense?.minuteNaissance) && (
                    <>
                      {' '}
                      <b>à</b>{' '}
                      {String(recense?.heureNaissance).padStart(2, '0')}:
                      {String(recense?.minuteNaissance).padStart(2, '0')}
                    </>
                  )}
              </span>
            </Grid.Column>
            <Grid.Column
              mobile={16}
              tablet={4}
              computer={8}
              largeScreen={3}
              widescreen={4}
            >
              <span>
                <b>Civilité : </b>
              </span>
              <span>{recense?.sexe === 'homme' ? 'Monsieur' : 'Madame'}</span>
            </Grid.Column>
            <Grid.Column
              mobile={16}
              tablet={5}
              computer={8}
              largeScreen={3}
              widescreen={4}
            >
              <span>
                <b>Nom (nom d&apos;usage) : </b>
              </span>
              <span>{`${_.toUpper(recense?.nomFamille)} ${!_.isEmpty(recense?.nomUsage) ? `(${recense?.nomUsage})` : ''}`}</span>
            </Grid.Column>
            <Grid.Column
              mobile={16}
              tablet={11}
              computer={8}
              largeScreen={5}
              widescreen={4}
            >
              <span>
                <b>Prénoms : </b>
              </span>
              <span>
                {`${recense?.prenom}${!_.isEmpty(recense?.prenomsComplementaires) ? `, ${recense?.prenomsComplementaires}` : ''}`}
              </span>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column
              mobile={16}
              tablet={12}
              computer={8}
              largeScreen={5}
              widescreen={4}
            >
              <span>
                <b>Département : </b>
              </span>
              <span>{formattedDepartement}</span>
            </Grid.Column>
            <Grid.Column
              mobile={16}
              tablet={4}
              computer={8}
              largeScreen={6}
              widescreen={4}
            >
              <span>
                <b>Commune (Pays) : </b>
              </span>
              <span>{`${libelleCodePostal || ''} ${libelleCommune || ''}${libellePays ? ` (${libellePays})` : ''}`}</span>
            </Grid.Column>
            {parent && (
              <Grid.Column
                mobile={16}
                tablet={4}
                computer={8}
                largeScreen={5}
                widescreen={6}
              >
                <span>
                  <b>Nationalité : </b>
                </span>
                <span>{recense?.nationalite?.nationaliteFeminin}</span>
              </Grid.Column>
            )}
          </Grid.Row>
        </>
      ) : (
        <Grid.Row />
      )}
    </>
  );
};

export default InformationIndividu;
